import React, { useState, useEffect } from 'react';
import { Filter, X, ChevronDown, ChevronUp, RefreshCw, Search } from 'lucide-react';
import './AdvancedFilters.css';

/**
 * Componente riutilizzabile per filtri avanzati nelle liste admin
 * 
 * Supporta:
 * - Query params per condivisione link
 * - Diversi tipi di filtri (select, multi-select, date range, number range, boolean, text)
 * - Reset filtri
 * - UI collassabile
 * 
 * @param {Object} props
 * @param {Array} props.filters - Configurazione filtri
 * @param {Function} props.onApply - Callback quando filtri vengono applicati (riceve object con filtri)
 * @param {Number} props.resultCount - Numero risultati trovati
 * 
 * Esempio configurazione filtri:
 * [
 *   {
 *     id: 'stato',
 *     label: 'Stato',
 *     type: 'select',
 *     options: [
 *       { value: 'ATTIVO', label: 'Attivo' },
 *       { value: 'SCADUTO', label: 'Scaduto' }
 *     ]
 *   },
 *   {
 *     id: 'stati',
 *     label: 'Stati',
 *     type: 'multi-select',
 *     options: [...]
 *   },
 *   {
 *     id: 'data',
 *     label: 'Data',
 *     type: 'date-range'
 *   },
 *   {
 *     id: 'importo',
 *     label: 'Importo',
 *     type: 'number-range'
 *   },
 *   {
 *     id: 'verificato',
 *     label: 'Verificato',
 *     type: 'boolean'
 *   },
 *   {
 *     id: 'search',
 *     label: 'Cerca',
 *     type: 'text',
 *     placeholder: 'Nome, cognome, codice...'
 *   }
 * ]
 */
const AdvancedFilters = ({ filters, onApply, resultCount }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // La ricerca sta sempre in vista, fuori dal pannello: è il comando
  // che si usa più spesso e non ha senso tenerlo dietro un clic.
  const filtroRicerca = filters.find((f) => f.id === 'search');
  const altriFiltri = filters.filter((f) => f.id !== 'search');

  // Inizializza filtri da query params all'avvio
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initialValues = {};
    
    filters.forEach(filter => {
      const paramValue = params.get(filter.id);
      
      if (paramValue) {
        switch (filter.type) {
          case 'multi-select':
            initialValues[filter.id] = paramValue.split(',');
            break;
          case 'date-range':
            initialValues[`${filter.id}_inizio`] = params.get(`${filter.id}_inizio`) || '';
            initialValues[`${filter.id}_fine`] = params.get(`${filter.id}_fine`) || '';
            break;
          case 'number-range':
            initialValues[`${filter.id}_min`] = params.get(`${filter.id}_min`) || '';
            initialValues[`${filter.id}_max`] = params.get(`${filter.id}_max`) || '';
            break;
          case 'boolean':
            initialValues[filter.id] = paramValue === 'true';
            break;
          default:
            initialValues[filter.id] = paramValue;
        }
      }
    });

    if (Object.keys(initialValues).length > 0) {
      setFilterValues(initialValues);
      setIsExpanded(true);
      // Applica automaticamente filtri da URL
      setTimeout(() => applyFilters(initialValues), 100);
    }
  }, []);

  // Conta filtri attivi
  useEffect(() => {
    let count = 0;
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === 'boolean') count++;
        else if (value) count++;
      }
    });
    setActiveFilterCount(count);
  }, [filterValues]);

  // Il numero sul pulsante non conta la ricerca: quella è già in vista
  const conteggioAltriFiltri = altriFiltri.reduce((n, f) => {
    const v = filterValues[f.id];
    if (f.type === 'multi-select') return n + ((v || []).length ? 1 : 0);
    if (f.type === 'boolean') return n + (v ? 1 : 0);
    if (f.type === 'date-range') {
      return n + ((filterValues[`${f.id}_inizio`] || filterValues[`${f.id}_fine`]) ? 1 : 0);
    }
    if (f.type === 'number-range') {
      return n + ((filterValues[`${f.id}_min`] || filterValues[`${f.id}_max`]) ? 1 : 0);
    }
    return n + (v ? 1 : 0);
  }, 0);

  // La ricerca si applica da sola poco dopo l'ultima battitura
  const primoRender = React.useRef(true);
  useEffect(() => {
    if (primoRender.current) {
      primoRender.current = false;
      return;
    }
    const t = setTimeout(() => applyFilters(filterValues, true), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues.search]);

  // Toglie un filtro e riapplica subito: una pastiglia rimossa deve
  // aggiornare l'elenco, non limitarsi a svuotare il campo nel pannello
  const rimuoviFiltro = (nuoviValori) => {
    setFilterValues(nuoviValori);
    applyFilters(nuoviValori);
  };

  // Descrive i filtri attivi in pastiglie leggibili, così restano visibili
  // anche a pannello chiuso e si possono togliere una alla volta
  const filtriAttivi = () => {
    const voci = [];
    filters.forEach((filtro) => {
      const v = filterValues[filtro.id];
      const etichettaOpzione = (val) =>
        filtro.options?.find((o) => String(o.value) === String(val))?.label ?? val;

      if (filtro.type === 'multi-select') {
        (v || []).forEach((val) =>
          voci.push({
            chiave: `${filtro.id}:${val}`,
            testo: `${filtro.label}: ${etichettaOpzione(val)}`,
            rimuovi: () => rimuoviFiltro({
              ...filterValues,
              [filtro.id]: (v || []).filter((x) => x !== val),
            }),
          })
        );
      } else if (filtro.type === 'boolean') {
        if (v) {
          voci.push({
            chiave: filtro.id,
            testo: filtro.label,
            rimuovi: () => rimuoviFiltro({ ...filterValues, [filtro.id]: false }),
          });
        }
      } else if (filtro.type === 'date-range' || filtro.type === 'number-range') {
        const suffissi = filtro.type === 'date-range' ? ['inizio', 'fine'] : ['min', 'max'];
        const da = filterValues[`${filtro.id}_${suffissi[0]}`];
        const a = filterValues[`${filtro.id}_${suffissi[1]}`];
        if (da || a) {
          voci.push({
            chiave: filtro.id,
            testo: `${filtro.label}: ${da || '…'} – ${a || '…'}`,
            rimuovi: () => rimuoviFiltro({
              ...filterValues,
              [`${filtro.id}_${suffissi[0]}`]: '',
              [`${filtro.id}_${suffissi[1]}`]: '',
            }),
          });
        }
      } else if (v) {
        voci.push({
          chiave: filtro.id,
          testo: `${filtro.label}: ${filtro.type === 'select' ? etichettaOpzione(v) : v}`,
          rimuovi: () => rimuoviFiltro({ ...filterValues, [filtro.id]: '' }),
        });
      }
    });
    return voci;
  };

  const handleChange = (filterId, value, isRange = false, rangeType = null) => {
    setFilterValues(prev => {
      if (isRange) {
        return {
          ...prev,
          [`${filterId}_${rangeType}`]: value
        };
      }
      return {
        ...prev,
        [filterId]: value
      };
    });
  };

  const handleMultiSelectChange = (filterId, optionValue) => {
    setFilterValues(prev => {
      const currentValues = prev[filterId] || [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      
      return {
        ...prev,
        [filterId]: newValues
      };
    });
  };

  const applyFilters = (values = filterValues, sostituisciCronologia = false) => {
    // Aggiorna URL con query params
    const params = new URLSearchParams();
    
    Object.entries(values).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(','));
        } else if (typeof value === 'boolean') {
          params.set(key, value.toString());
        } else if (value) {
          params.set(key, value);
        }
      }
    });

    // Aggiorna URL senza ricaricare pagina
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    if (sostituisciCronologia) {
      // La ricerca si applica a ogni battitura: impilare una voce di
      // cronologia per lettera renderebbe inutilizzabile il tasto Indietro
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }

    // Chiama callback con filtri
    onApply(values);
  };

  const resetFilters = () => {
    setFilterValues({});
    window.history.pushState({}, '', window.location.pathname);
    onApply({});
  };

  const renderFilter = (filter) => {
    switch (filter.type) {
      case 'select':
        return (
          <div key={filter.id} className="filter-field">
            <label className="filter-label">{filter.label}</label>
            <select
              value={filterValues[filter.id] || ''}
              onChange={(e) => handleChange(filter.id, e.target.value)}
              className="filter-input"
            >
              <option value="">Tutti</option>
              {filter.options.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );

      case 'multi-select':
        return (
          <div key={filter.id} className="filter-field">
            <label className="filter-label">{filter.label}</label>
            <div className="multi-select-container">
              {filter.options.map(opt => (
                <label key={opt.value} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={(filterValues[filter.id] || []).includes(opt.value)}
                    onChange={() => handleMultiSelectChange(filter.id, opt.value)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'date-range':
        return (
          <div key={filter.id} className="filter-field filter-range">
            <label className="filter-label">{filter.label}</label>
            <div className="range-inputs">
              <input
                type="date"
                value={filterValues[`${filter.id}_inizio`] || ''}
                onChange={(e) => handleChange(filter.id, e.target.value, true, 'inizio')}
                className="filter-input range-input"
                placeholder="Da"
              />
              <span className="range-separator">—</span>
              <input
                type="date"
                value={filterValues[`${filter.id}_fine`] || ''}
                onChange={(e) => handleChange(filter.id, e.target.value, true, 'fine')}
                className="filter-input range-input"
                placeholder="A"
              />
            </div>
          </div>
        );

      case 'number-range':
        return (
          <div key={filter.id} className="filter-field filter-range">
            <label className="filter-label">{filter.label}</label>
            <div className="range-inputs">
              <input
                type="number"
                value={filterValues[`${filter.id}_min`] || ''}
                onChange={(e) => handleChange(filter.id, e.target.value, true, 'min')}
                className="filter-input range-input"
                placeholder="Min"
                step={filter.step || 0.01}
              />
              <span className="range-separator">—</span>
              <input
                type="number"
                value={filterValues[`${filter.id}_max`] || ''}
                onChange={(e) => handleChange(filter.id, e.target.value, true, 'max')}
                className="filter-input range-input"
                placeholder="Max"
                step={filter.step || 0.01}
              />
            </div>
          </div>
        );

      case 'boolean':
        return (
          <div key={filter.id} className="filter-field">
            <label className="checkbox-label checkbox-label-main">
              <input
                type="checkbox"
                checked={filterValues[filter.id] || false}
                onChange={(e) => handleChange(filter.id, e.target.checked)}
                className="checkbox-input"
              />
              <span className="checkbox-text">{filter.label}</span>
            </label>
          </div>
        );

      case 'text':
        return (
          <div key={filter.id} className="filter-field">
            <label className="filter-label">{filter.label}</label>
            <input
              type="text"
              value={filterValues[filter.id] || ''}
              onChange={(e) => handleChange(filter.id, e.target.value)}
              className="filter-input"
              placeholder={filter.placeholder || ''}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {/* Barra: ricerca sempre visibile, poi il pulsante dei filtri */}
      <div className="filters-bar">
        {filtroRicerca && (
          <div className="search-wrap">
            <Search className="search-icon" />
            <input
              type="text"
              value={filterValues.search || ''}
              onChange={(e) => handleChange('search', e.target.value)}
              placeholder={filtroRicerca.placeholder || 'Cerca...'}
              className="search-input"
            />
            {filterValues.search && (
              <button
                type="button"
                className="search-clear"
                onClick={() => rimuoviFiltro({ ...filterValues, search: '' })}
                aria-label="Cancella ricerca"
              >
                <X className="btn-icon" />
              </button>
            )}
          </div>
        )}

        {altriFiltri.length > 0 && (
          <button
            type="button"
            className={`btn-filtri ${isExpanded ? 'aperto' : ''}`}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Filter className="btn-icon" />
            <span>Filtri</span>
            {conteggioAltriFiltri > 0 && (
              <span className="active-filters-badge">{conteggioAltriFiltri}</span>
            )}
            {isExpanded ? <ChevronUp className="chevron-icon" /> : <ChevronDown className="chevron-icon" />}
          </button>
        )}

        {resultCount !== undefined && resultCount !== null && (
          <span className="results-count">
            {resultCount} {resultCount === 1 ? 'risultato' : 'risultati'}
          </span>
        )}
      </div>

      {/* Filtri attivi: visibili anche a pannello chiuso */}
      {activeFilterCount > 0 && (
        <div className="filters-chips">
          {filtriAttivi().map((voce) => (
            <button
              key={voce.chiave}
              type="button"
              className="filter-chip"
              onClick={() => voce.rimuovi()}
              title="Rimuovi questo filtro"
            >
              <span>{voce.testo}</span>
              <X className="chip-x" />
            </button>
          ))}
          <button type="button" className="filter-chip filter-chip-reset" onClick={resetFilters}>
            Azzera tutti
          </button>
        </div>
      )}

      {/* Filter Fields */}
      {isExpanded && (
        <div className="filters-body">
          <div className="filters-grid">
            {altriFiltri.map(filter => renderFilter(filter))}
          </div>

          {/* Actions */}
          <div className="filters-actions">
            <button
              onClick={() => applyFilters()}
              className="btn-apply"
            >
              <Filter className="btn-icon" />
              Applica Filtri
            </button>
            <button
              onClick={resetFilters}
              className="btn-reset"
            >
              <X className="btn-icon" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilters;
